// Platform admin access for internal tools, alongside the separate named-account
// policies for consent, deal caps, and commercial pipeline access.
export const FOUNDERS = [
    { email: 'donna@internationalpropertymarket.com', name: 'Donna', first: 'Donna' },
];

// Also hide privileged tools from stale browser sessions for retired operators.
// The server independently checks the acting account's current role and session.
export const RETIRED_OPERATOR_EMAILS = [
    'corne@internationalpropertymarket.com',
    'marius@internationalpropertymarket.com',
    'marius@ikasi.ai',
];
const RETIRED_OPERATOR_SET = new Set(RETIRED_OPERATOR_EMAILS);

// Who may VIEW and CHANGE a client's consent (marketing + document-sharing) on
// the client profile / edit-lead views. Locked to the company owner and backup
// administrator - no agency staff or other admins can alter a
// recorded consent decision after the fact. Agents can still capture consent at
// lead onboarding and at the point of sharing documents (those are audited,
// point-in-time attestations); this list only governs the standing override.
export const CONSENT_MANAGER_EMAILS = [
    'infrastructure@internationalpropertymarket.com',
    'stehan@internationalpropertymarket.com',
];
const CONSENT_MANAGER_SET = new Set(CONSENT_MANAGER_EMAILS.map((e) => e.toLowerCase()));

/** IPM commercial pipeline (not agency Sales). Shared board for these admin seats only. */
export const IPM_SALES_EMAILS = [
    'stehan@internationalpropertymarket.com',
    'donna@internationalpropertymarket.com',
    'infrastructure@internationalpropertymarket.com',
    'heila@internationalpropertymarket.com',
];
const IPM_SALES_SET = new Set(IPM_SALES_EMAILS.map((e) => e.toLowerCase()));

/** Who may raise, lower, or remove the yearly deal cap. Not other admins, not enterprise clients. */
export const DEAL_CAP_MANAGER_EMAILS = [
    'stehan@internationalpropertymarket.com',
    'admin@internationalpropertymarket.com',
    'donna@internationalpropertymarket.com',
    'infrastructure@internationalpropertymarket.com',
];
const DEAL_CAP_MANAGER_SET = new Set(DEAL_CAP_MANAGER_EMAILS.map((e) => e.toLowerCase()));

const normEmail = (e) => String(e || '').toLowerCase().trim();
export const isPlatformAdmin = (user) => !!user && String(user.role || '').toLowerCase() === 'admin'
    && !RETIRED_OPERATOR_SET.has(normEmail(user.email));

/** True only for the owner(s) allowed to view/change standing consent decisions. */
export function canManageConsent(user) {
    return isPlatformAdmin(user) && CONSENT_MANAGER_SET.has(normEmail(user.email));
}

export function getCurrentUser() {
    try {
        return JSON.parse(localStorage.getItem('user'));
    } catch (_) {
        return null;
    }
}

export function canSeeCostModel(user) {
    return isPlatformAdmin(user);
}

// Scheduler management follows the signed-in platform admin role. The server
// independently resolves the acting account's current role from its user id.
export function canManageScheduler(user) {
    return isPlatformAdmin(user);
}

/** Live cluster metrics on Admin → Ops require the acting platform admin role. */
export function canSeeOpsHealth(user) {
    return isPlatformAdmin(user);
}

export function canSeeRuntimeIdentity(user) {
    return isPlatformAdmin(user);
}

export function canChangeDealCaps(user) {
    return isPlatformAdmin(user) && DEAL_CAP_MANAGER_SET.has(normEmail(user.email));
}

/** IPM sales CRM for the company's authorised commercial administrators. */
export function canSeeIpmSales(user) {
    return isPlatformAdmin(user) && IPM_SALES_SET.has(normEmail(user.email));
}

// The founder(s) a given user can send scenarios to (everyone except themselves).
export function recipientFounders(user) {
    const me = normEmail(user && user.email);
    return FOUNDERS.filter((f) => f.email !== me);
}
